import React from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { coreFeatures, iamFeatures, pipelineStages, candidateFeatures } from '../data/mock';
import { Shield, GitBranch, Users, FileCheck, Sparkles, Video, Building2, User, Check, Lock, FileText, ClipboardCheck, MessageSquare, FileSignature, ArrowRight, Database, Brain, Filter, Mail } from 'lucide-react';

const iconMap = { Shield, GitBranch, Users, FileCheck, Sparkles, Video, Database, Brain, Filter, Mail };
const stageIcons = [FileText, ClipboardCheck, MessageSquare, FileSignature];

const FeaturesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">The Sandgen Suite</span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-black leading-[0.95] mb-6 tracking-tight">
              Everything.
              <br />
              <span className="text-neutral-400">Perfected.</span>
            </h1>
            <p className="text-xl text-neutral-500 leading-relaxed">
              Six pillars of hiring excellence. Each one best-in-class. 
              Together, unstoppable.
            </p>
          </div>
        </div>
      </section>

      {/* Core Features Grid */}
      <section className="section bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12">
          <h2 className="font-display text-3xl md:text-4xl text-black mb-4">The Six Pillars</h2>
          <p className="text-neutral-500 mb-12 max-w-2xl">Each component engineered for excellence. Accept no substitutes.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreFeatures.map((feature, index) => {
              const Icon = iconMap[feature.icon];
              return (
                <div key={feature.id} className="group bg-white border border-neutral-200 p-10 hover:border-neutral-300 hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                  <span className="absolute top-8 right-8 text-7xl font-bold text-neutral-100 select-none group-hover:text-neutral-200 transition-colors">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="relative z-10">
                    <div className="w-12 h-12 bg-neutral-100 border border-neutral-200 rounded-xl flex items-center justify-center mb-8 group-hover:bg-black group-hover:border-black group-hover:text-white transition-all duration-300">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-black text-xl font-semibold mb-4">{feature.title}</h3>
                    <p className="text-neutral-500 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sandgen Vault */}
      <section className="section bg-black relative">
        <div className="absolute inset-0 grid-bg-dark" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl mb-16">
            <span className="text-neutral-500 text-sm font-medium uppercase tracking-widest mb-4 block">Sandgen Vault</span>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[1] mb-6 tracking-tight">
              Security so good, <span className="text-neutral-500">you&apos;ll forget it&apos;s there.</span>
            </h2>
            <p className="text-lg text-neutral-400">
              Enterprise-grade protection that doesn&apos;t get in the way. Because security should be invisible, not insufferable.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-neutral-900 border border-neutral-800 p-10">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h3 className="text-white text-xl font-semibold">For Your Team</h3>
                  <p className="text-neutral-500 text-sm">The people who make hiring happen</p>
                </div>
              </div>
              <ul className="space-y-4">
                {iamFeatures.organization.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-5 h-5 bg-white/10 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-white" />
                    </span>
                    <span className="text-neutral-300">{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-neutral-800">
                <p className="text-neutral-500 text-sm mb-3">Role Hierarchy</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-white text-black text-sm font-medium rounded-full">Admin</span>
                  <span className="px-3 py-1 bg-neutral-800 text-white text-sm font-medium rounded-full">Member</span>
                  <span className="px-3 py-1 bg-neutral-800 text-white text-sm font-medium rounded-full">Panelist</span>
                </div>
              </div>
            </div>

            <div className="bg-neutral-900 border border-neutral-800 p-10">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                  <User className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h3 className="text-white text-xl font-semibold">For Candidates</h3>
                  <p className="text-neutral-500 text-sm">The talent you&apos;re trying to impress</p>
                </div>
              </div>
              <ul className="space-y-4">
                {iamFeatures.candidate.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-5 h-5 bg-white/10 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-white" />
                    </span>
                    <span className="text-neutral-300">{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-neutral-800">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-neutral-400" />
                  <span className="text-neutral-400 text-sm">OAuth 2.0 & Enterprise SSO. Obviously.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sandgen Flow */}
      <section className="section bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">Sandgen Flow</span>
            <h2 className="font-display text-4xl md:text-5xl text-black leading-[1] mb-6 tracking-tight">
              Your pipeline. <span className="text-neutral-400">Your rules.</span>
            </h2>
            <p className="text-lg text-neutral-500">
              Configure stages that match how you actually work. Not the other way around.
            </p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-neutral-300 -translate-y-1/2" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
              {pipelineStages.map((item, index) => {
                const Icon = stageIcons[index];
                return (
                  <div key={index} className="relative">
                    <div className="bg-white border border-neutral-200 p-8 text-center group hover:border-neutral-300 hover:shadow-lg transition-all duration-300">
                      <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-black transition-colors relative z-10">
                        <Icon className="w-7 h-7 text-neutral-600 group-hover:text-white transition-colors" />
                      </div>
                      <div className="text-neutral-400 text-xs font-semibold uppercase tracking-widest mb-2">Stage {index + 1}</div>
                      <h3 className="text-black text-lg font-semibold mb-3">{item.stage}</h3>
                      <p className="text-neutral-500 text-sm leading-relaxed">{item.description}</p>
                    </div>
                    {index < pipelineStages.length - 1 && (
                      <div className="hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-20">
                        <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center border border-neutral-200">
                          <ArrowRight className="w-4 h-4 text-neutral-400" />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Sandgen Talent Pool */}
      <section className="section bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">Sandgen Talent Pool</span>
              <h2 className="font-display text-4xl md:text-5xl text-black leading-[1] mb-6 tracking-tight">
                Every candidate. <span className="text-neutral-400">One truth.</span>
              </h2>
              <p className="text-lg text-neutral-500 mb-8">
                A single source of truth for all your talent. Searchable. Filterable. 
                Impossibly organized.
              </p>
              <div className="flex flex-wrap gap-3">
                {['Neural Parsing', 'Smart Filters', 'Bulk Actions', 'Blacklist Controls'].map((tag) => (
                  <span key={tag} className="px-4 py-2 bg-neutral-100 border border-neutral-200 text-neutral-700 text-sm rounded-full">{tag}</span>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {candidateFeatures.map((feature, index) => {
                const Icon = iconMap[feature.icon];
                return (
                  <div key={index} className="bg-neutral-50 border border-neutral-100 p-8 group hover:bg-white hover:border-neutral-200 hover:shadow-lg transition-all duration-300">
                    <div className="w-10 h-10 bg-white border border-neutral-200 rounded-lg flex items-center justify-center mb-6 group-hover:bg-black group-hover:border-black group-hover:text-white transition-all duration-300">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="text-black font-semibold mb-2">{feature.title}</h3>
                    <p className="text-neutral-500 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeaturesPage;
